/**
 * smoke_columns.ts — LARGE SMOKE COLUMNS (fire realism R10, 2026-09-25): battles, crash sites, industry.
 *
 * A battle raging over a town, a downed gunship burning in a field, the ore works' chimneys: the world implies
 * big smoke and drew none. The Battlefield: Bad Company 2 recipe — a TALL cylindrical billboard far away (a card
 * that turns about Y only, so the column stays upright however you look at it), a scrolling noise eating its
 * edges so it billows upward, widening and leaning downwind with height, lit by the same sky light the six-way
 * smoke uses (`uSmokeLight`), glowing orange at the base while its fire burns, soft-faded where it meets the
 * ground. NEAR a column (≤ 150 m) the fire system's six-way / card smoke takes over (its owner registers a
 * smoke-only fire) and the far card fades out.
 *
 * ONE instanced batch for every column in view: an InstancedBufferGeometry of one card with two per-instance
 * vec4s (centre + height, width + seed + strength + glow) — three vertex buffers, a fixed count (the cache-key
 * law: unused slots are zero-height), premultiplied OVER, `markMrtOverlay`, positionPrevious pinned.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, float, vec3, vec4, attribute, uv, texture3D, positionGeometry, positionPrevious, cameraPosition, modelWorldMatrixInverse,
  atan, cos, sin, smoothstep, saturate, exp, abs, mix, cameraNear, cameraFar, positionView, time, uniform,
} from 'three/tsl';
import { vfxSceneLinearDepth } from './scene_depth.js';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { fireTextures, uSmokeLight } from './fire_volume.js';

export const SMOKE_COLUMN_CAP = 24;
/** Inside this the owner's near smoke (six-way / cards) carries the column. */
export const SMOKE_COLUMN_NEAR_M = 150;

export interface SmokeColumnSource {
  key: string;
  /** ABSOLUTE base. */
  x: number; y: number; z: number;
  /** Height (m) and width at the base (m). */
  height: number; width: number;
  /** 0..1 density (a fading record smokes less). */
  strength: number;
  /** 0..1 the fire under it (the base glows). */
  glow: number;
}

export class SmokeColumns {
  readonly mesh: THREE.Mesh;
  private readonly a0: Float32Array;
  private readonly a1: Float32Array;
  private readonly attr0: THREE.InstancedBufferAttribute;
  private readonly attr1: THREE.InstancedBufferAttribute;
  private readonly uWind = uniform(new THREE.Vector3(1, 0, 0));
  count = 0;

  constructor() {
    const g = new THREE.InstancedBufferGeometry();
    const card = new THREE.PlaneGeometry(1, 1);
    card.translate(0, 0.5, 0); // base at y = 0
    g.setAttribute('position', card.getAttribute('position'));
    g.setAttribute('uv', card.getAttribute('uv'));
    g.setIndex(card.getIndex());
    this.a0 = new Float32Array(SMOKE_COLUMN_CAP * 4);
    this.a1 = new Float32Array(SMOKE_COLUMN_CAP * 4);
    this.attr0 = new THREE.InstancedBufferAttribute(this.a0, 4);
    this.attr1 = new THREE.InstancedBufferAttribute(this.a1, 4);
    this.attr0.setUsage(THREE.DynamicDrawUsage); this.attr1.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('aCol', this.attr0);
    g.setAttribute('aCol2', this.attr1);
    g.instanceCount = SMOKE_COLUMN_CAP; // FIXED (the cache-key law); unused slots have zero height
    const m = new THREE.NodeMaterial();
    m.name = 'smoke-columns';
    m.transparent = true; m.depthWrite = false; m.fog = false; m.side = THREE.DoubleSide;
    m.blending = THREE.CustomBlending;
    m.blendSrc = THREE.OneFactor; m.blendDst = THREE.OneMinusSrcAlphaFactor;
    m.blendSrcAlpha = THREE.OneFactor; m.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
    const c0 = attribute('aCol', 'vec4'), c1 = attribute('aCol2', 'vec4');
    m.positionNode = Fn(() => {
      // Measured in GEOMETRY space (the positionGeometry law): the card turns about Y toward the camera.
      const camL = modelWorldMatrixInverse.mul(vec4(cameraPosition, 1)).xyz;
      const yaw = atan(camL.x.sub(c0.x), camL.z.sub(c0.z));
      const h = c0.w, w = c1.x;
      // Lean downwind with height (the wind's xz, × height²).
      const lean = vec3(this.uWind.x, 0, this.uWind.z).mul(positionGeometry.y.mul(positionGeometry.y)).mul(h.mul(0.22));
      const x = positionGeometry.x.mul(w).mul(float(1).add(positionGeometry.y.mul(1.6)));
      const p = vec3(c0.x, c0.y, c0.z).add(vec3(x.mul(cos(yaw)), positionGeometry.y.mul(h), x.mul(sin(yaw)).negate())).add(lean);
      positionPrevious.assign(p);
      return p;
    })();
    m.colorNode = Fn(() => {
      const seed = c1.y, strength = c1.z, glow = c1.w;
      const u0 = uv();
      const x = u0.x.sub(0.5).mul(2), y = u0.y;
      const t = time.mul(0.018);
      const w = texture3D(fireTextures().noise, vec3(x.mul(0.3).add(seed), y.mul(1.1).sub(t), seed.mul(0.37))).xyz.sub(0.5).mul(2);
      const n = texture3D(fireTextures().noise, vec3(x.mul(0.5).add(seed.mul(1.9)).add(w.x.mul(0.2)), y.mul(2.2).sub(t.mul(1.7)), t.mul(0.3).add(seed))).w;
      const edge = abs(x.add(w.x.mul(0.3)));
      const body = float(1).sub(smoothstep(0.35, 0.95, edge)).mul(smoothstep(0.0, 0.05, y)).mul(float(1).sub(smoothstep(0.62, 1.0, y)));
      const dens = saturate(body.mul(n.mul(1.9).sub(0.4).add(w.y.mul(0.25)))).mul(strength);
      const a = saturate(dens.mul(0.8));
      // Darker, browner at the base (fresh combustion smoke), greyer and thinner aloft; the base glows over a fire.
      const base = mix(vec3(0.26, 0.23, 0.2), vec3(0.62, 0.61, 0.6), smoothstep(0.05, 0.7, y));
      const lit = uSmokeLight.mul(2.4).mul(base).add(vec3(1.0, 0.42, 0.12).mul(glow).mul(exp(y.mul(-14))).mul(3));
      const sceneDepth = vfxSceneLinearDepth.mul(cameraFar.sub(cameraNear)).add(cameraNear); // the VFX's own snapshot (scene_depth.ts)
      const fade = saturate(sceneDepth.sub(positionView.z.negate()).div(6));
      const aa = a.mul(fade);
      return vec4(lit.mul(aa), aa);
    })();
    markMrtOverlay(m);
    this.mesh = new THREE.Mesh(g, m);
    this.mesh.name = 'smoke-columns';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 8;
    this.mesh.visible = false;
  }

  /** Write the columns in view (nearest first), fading out the near ones the owner's near smoke carries. */
  write(sources: SmokeColumnSource[], cam: { x: number; z: number }, wind: { x: number; z: number; s: number }): void {
    (this.uWind.value as THREE.Vector3).set(wind.x * Math.min(1.5, 0.3 + wind.s), 0, wind.z * Math.min(1.5, 0.3 + wind.s));
    const sorted = sources.map((s) => ({ s, d: Math.hypot(s.x - cam.x, s.z - cam.z) })).sort((a, b) => a.d - b.d);
    let n = 0;
    for (const { s, d } of sorted) {
      if (n >= SMOKE_COLUMN_CAP) break;
      const nearFade = Math.min(1, Math.max(0, (d - SMOKE_COLUMN_NEAR_M * 0.6) / (SMOKE_COLUMN_NEAR_M * 0.4)));
      if (nearFade <= 0.01) continue;
      const o = n * 4;
      this.a0[o] = s.x; this.a0[o + 1] = s.y; this.a0[o + 2] = s.z; this.a0[o + 3] = s.height;
      this.a1[o] = s.width; this.a1[o + 1] = (hash(s.key) % 997) / 97; this.a1[o + 2] = s.strength * nearFade; this.a1[o + 3] = s.glow;
      n++;
    }
    for (let i = n; i < SMOKE_COLUMN_CAP; i++) this.a0[i * 4 + 3] = 0;
    this.attr0.needsUpdate = true; this.attr1.needsUpdate = true;
    this.count = n;
    this.mesh.visible = n > 0;
  }
}

function hash(s: string): number { let h = 0x811c9dc5; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); } return h >>> 0; }
